<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Edit Opportunity
            </h3>

            <p>
              Edit the details of the opportunity.
            </p>

            <div class="btn-group">
              <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-cogs"></i> Actions <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li>
                  <router-link
                      :to="'/companies/' + company.data.id + '/orders/create?opportunity=' + opportunityId"
                  >
                    <i class="fa fa-dollar-sign"></i> Convert to order
                  </router-link>
                </li>

                <li>
                  <router-link
                      :to="'/companies/' + company.data.id + '/subscriptions/create?opportunity=' + opportunityId"
                  >
                    <i class="fa fa-dollar-sign"></i> Convert to subscription
                  </router-link>
                </li>

                <li>
                  <a @click.prevent="close">
                    <i class="fa fa-times"></i> Close
                  </a>
                </li>
              </ul>
            </div>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <text-form-group
                    v-model="data.title"
                    name="title"
                    label="Title"
                    help-text="A title for the opportunity."
                    :error="getError('title')"
                />

                <number-form-group
                    v-model="data.value"
                    name="value"
                    label="Value"
                    help-text="The value of the opportunity."
                    :error="getError('value')"
                    :disabled="data.closed_at"
                />

                <select-form-group
                    v-model="data.category"
                    name="category"
                    label="Category"
                    help-text="The category that the opportunity belongs to."
                    :error="getError('category')"
                    :options="[{value: 'advertising', label: 'Advertising'}, {value: 'subscription', label: 'Subscription'}]"
                />

                <date-form-group
                    v-model="data.conversion_expected_at"
                    name="conversion_expected_at"
                    label="Conversion Expected At"
                    help-text="The expected date that the opportunity will be converted."
                    :error="getError('conversion_expected_at')"
                    format="DD.MM.YYYY"
                />

                <select-form-group
                    v-model="data.user_id"
                    name="user_id"
                    label="User"
                    help-text="The user that is responsible for the opportunity."
                    :error="getError('user_id')"
                    :options="userOptions"
                />

                <select-form-group
                    v-model="data.portal_id"
                    name="portal_id"
                    label="Portal"
                    help-text="The portal that the opportunity relates to."
                    :error="getError('portal_id')"
                    :options="portalOptions"
                />

                <select-with-button-form-group
                    v-model="data.contact_person_id"
                    name="contact_person_id"
                    label="Contact Person"
                    help-text="The contact person in the company."
                    :error="getError('contact_person_id')"
                    :options="personOptions"
                    :nullable="true"
                    @click="openPersonModal"
                />

                <select-form-group
                    v-model="data.project_id"
                    name="project_id"
                    label="Project"
                    help-text="The project that the opportunity relates to."
                    :error="getError('project_id')"
                    :options="projectOptions"
                    :nullable="true"
                />

                <select-form-group
                    v-model="data.meeting_id"
                    name="meeting_id"
                    label="Meeting"
                    help-text="The meeting that the opportunity was created from."
                    :error="getError('meeting_id')"
                    :options="meetingOptions"
                    :nullable="true"
                />

                <radio-form-group
                    v-model="data.is_new_business"
                    name="is_new_business"
                    label="New Business?"
                    :error="getError('is_new_business')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                    :disabled="data.meeting_id"
                />

                <label-form-group
                    label="Status"
                    :value="statusLabelText"
                    help-text="The status of the opportunity."
                    :type="statusLabelType"
                />

                <static-form-group
                    label="Created At"
                    :value="data.created_at | datetime"
                />

                <hr>

                <submit-form-group button-class="btn-black"/>
              </form>
            </div>
          </div>
        </div>
      </div>

      <create-person-modal :company="company.data.id" @created="personCreated"></create-person-modal>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import notify from '@/notify'
import CreatePersonModal from '@/pages/Companies/Companies/Edit/Modals/CreatePerson'
import DateFormGroup from '@/vendor/forms/FormGroups/Horizontal/Date'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import LabelFormGroup from '@/vendor/forms/FormGroups/Horizontal/Label'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SelectWithButtonFormGroup from '@/components/FormGroups/Horizontal/SelectWithButton'
import StaticFormGroup from '@/vendor/forms/FormGroups/Horizontal/Static'
import StatusLabel from '@/components/StatusLabel'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'

export default {
  components: {
    CreatePersonModal,
    DateFormGroup,
    HelpText,
    LabelFormGroup,
    LoadingContainer,
    NumberFormGroup,
    RadioFormGroup,
    SelectFormGroup,
    SelectWithButtonFormGroup,
    StaticFormGroup,
    StatusLabel,
    SubmitFormGroup,
    TextFormGroup
  },

  mixins: [Errors],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company', 'opportunityId'],

  data() {
    return {
      data: null,

      users: null,
      portals: null,
      people: null,
      meetings: null,
      projects: null,

      loading: true
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    userOptions() {
      return this.users.map(user => ({value: user.id, label: user.attributes.full_name}))
    },

    portalOptions() {
      return this.portals.map(portal => ({value: portal.id, label: portal.attributes.name}))
    },

    personOptions() {
      return this.people
          .filter(person => person.is_active)
          .map(person => ({value: person.id, label: person.name}))
    },

    meetingOptions() {
      return this.meetings.map(meeting => (
          {
            value: meeting.id,
            label: (meeting.attributes.title || 'Untitled meeting') + ' (' + this.$options.filters.datetime(meeting.attributes.starts_at) + ')'
          }
      ))
    },

    projectOptions() {
      return this.projects.map(project => ({value: project.id, label: project.attributes.portal_name + ' - ' +project.attributes.project_category_name + ' - ' + project.attributes.name}))
    },

    statusLabelText() {
      if (this.data.status === 'open') {
        return 'Open'
      }

      if (this.data.status === 'missed') {
        return 'Missed'
      }

      if (this.data.status === 'converted') {
        return 'Converted'
      }

      return 'Unknown'
    },

    statusLabelType() {
      if (this.data.status === 'open') {
        return 'info'
      }

      if (this.data.status === 'missed') {
        return 'danger'
      }

      if (this.data.status === 'converted') {
        return 'success'
      }

      return 'primary'
    }
  },

  methods: {
    load() {
      this.loading = true

      Promise.all([
        http.get('/api/v2/opportunities/' + this.opportunityId),
        http.get('/api/v2/users'),
        http.get('/api/v2/portals'),
        http.get('/api/v1/companies/' + this.company.data.id + '/people'),
        http.get('/api/v2/meetings', {'page_size': 100, 'filter[company_id]': this.company.data.id}),
        http.get('/api/v2/projects', {'page_size': 100, 'filter[status]': 'active'}),
      ])
          .then(responses => {
            this.data = responses[0].data.data.attributes

            this.users = responses[1].data.data

            this.portals = responses[2].data.data

            this.people = responses[3].data.data

            this.meetings = responses[4].data.data

            this.projects = responses[5].data.data

            this.loading = false
          })
    },

    submit() {
      http.put('/api/v2/opportunities/' + this.opportunityId, this.data)
          .then(() => {
            notify.success('The opportunity was saved')

            this.errors = {}
          })
          .catch(this.catchValidationErrors)
    },

    close() {
      modal.confirm('Are you sure that you want to close the opportunity?', () => {
        http.post('/api/v2/opportunities/' + this.opportunityId + '/close')
            .then(response => {
              notify.success('The opportunity was closed')

              this.data = response.data.data
            })
      })
    },

    openPersonModal() {
      $('#create-person-modal').modal('show')
    },

    personCreated(person) {
      this.people.push(person)

      this.people.sort((a, b) => a.name.toLowerCase() >= b.name.toLowerCase() ? 1 : -1)

      this.$nextTick(() => {
        this.data.contact_person_id = person.id
      })
    }
  }
}
</script>
